import { useRef, useEffect, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { motion, AnimatePresence } from "framer-motion";
import { Send, StopCircle, Bot, Loader2, ChevronRight, X, Sparkles } from "lucide-react";
import { GlassCard } from "@/components/ui";
import { AgentConfirmationCard, extractConfirmation } from "@/components/AgentConfirmationCard";
import { getOrRefreshToken } from "@/lib/api";

// ════════════════════════════════════════════════════════════════════════════
// Flo AI — Floating prompt bar + modal chat
// ════════════════════════════════════════════════════════════════════════════

const API_BASE = import.meta.env.VITE_API_URL ?? "";

const SUGGESTED_PROMPTS = [
  "Show me today's appointments",
  "What's the no-show rate this month?",
  "List all active providers",
  "Summarize this week's performance",
];

// ─── Modal chat UI ─────────────────────────────────────────────────────────

function FloAgentModal({ onClose }: { onClose: () => void }) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [input, setInput] = useState("");

  const { messages, sendMessage, status, error, stop } = useChat({
    transport: new DefaultChatTransport({
      api: `${API_BASE}/api/agent/chat`,
      headers: async () => {
        const token = await getOrRefreshToken();
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        return headers;
      },
    }),
  });

  const isStreaming = status === "streaming";
  const isSubmitting = status === "submitted";
  const isBusy = isStreaming || isSubmitting;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isBusy) return;
    setInput("");
    sendMessage({ text });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggest = (prompt: string) => {
    if (isBusy) return;
    sendMessage({ text: prompt });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.97 }}
        transition={{ type: "spring", damping: 28, stiffness: 380 }}
        className="glass-card-elevated w-full sm:max-w-2xl h-[85vh] sm:h-[72vh] flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-af-900 dark:bg-af-100">
              <Bot className="w-4 h-4 text-white dark:text-af-900" />
            </div>
            <div>
              <p className="text-sm font-semibold text-af-900 dark:text-af-100">Flo</p>
              <p className="text-xs text-af-500">AI Business Assistant</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-af-500 hover:text-af-900 dark:hover:text-af-100"
          >
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
          {messages.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center h-full gap-5 text-center"
            >
              <div>
                <h2 className="text-lg font-semibold text-af-900 dark:text-af-100 mb-1">
                  Hi, I'm Flo
                </h2>
                <p className="text-sm text-af-600 dark:text-af-400 max-w-sm">
                  Ask me anything about your appointments, providers, or business performance.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-md">
                {SUGGESTED_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => handleSuggest(prompt)}
                    className="flex items-center gap-2 text-left px-3 py-2.5 rounded-xl bg-white/10 dark:bg-white/5 border border-white/15 hover:bg-white/20 dark:hover:bg-white/10 transition-colors text-sm text-af-800 dark:text-af-200"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-af-400 shrink-0" />
                    {prompt}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          <AnimatePresence initial={false}>
            {messages.map((msg) => {
              const isUser = msg.role === "user";
              const text = (msg.parts ?? [])
                .filter((p): p is { type: "text"; text: string } => p.type === "text")
                .map((p) => p.text)
                .join("\n\n")
                .trim();
              const confirmation = !isUser && msg.parts ? extractConfirmation(msg.parts as unknown[]) : null;

              if (!text && !confirmation && msg.role !== "user") return null;

              return (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex flex-col gap-2 ${isUser ? "items-end" : "items-start"}`}
                >
                  <div className={`flex ${isUser ? "justify-end" : "justify-start"} w-full`}>
                    {!isUser && (
                      <div className="flex items-end mr-2 mb-1">
                        <div className="w-7 h-7 rounded-lg bg-af-900 dark:bg-af-100 flex items-center justify-center shrink-0">
                          <Bot className="w-4 h-4 text-white dark:text-af-900" />
                        </div>
                      </div>
                    )}
                    {text && (
                      <GlassCard
                        className={`max-w-[78%] px-4 py-2.5 text-sm ${
                          isUser
                            ? "bg-af-900/90 dark:bg-af-100/90 text-white dark:text-af-900 rounded-2xl rounded-br-sm"
                            : "rounded-2xl rounded-bl-sm text-af-900 dark:text-af-100"
                        }`}
                      >
                        <div className="whitespace-pre-wrap leading-relaxed">{text}</div>
                      </GlassCard>
                    )}
                  </div>
                  {confirmation && (
                    <div className="ml-9">
                      <AgentConfirmationCard
                        actionId={confirmation.action_id}
                        actionType={confirmation.action_type}
                        preview={confirmation.preview}
                      />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>

          {isBusy && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-2"
            >
              <div className="w-7 h-7 rounded-lg bg-af-900 dark:bg-af-100 flex items-center justify-center">
                <Loader2 className="w-4 h-4 text-white dark:text-af-900 animate-spin" />
              </div>
              <span className="text-xs text-af-400">Flo is thinking…</span>
            </motion.div>
          )}

          {error && (
            <div className="text-red-500 text-sm px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
              {error.message}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 pb-4 pt-2 border-t border-white/10 shrink-0">
          <div className="flex items-end gap-2">
            <GlassCard className="flex-1 flex items-end gap-2 px-4 py-3 rounded-2xl">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Flo anything…"
                rows={1}
                className="flex-1 bg-transparent text-sm text-af-900 dark:text-af-100 placeholder:text-af-400 resize-none outline-none min-h-[1.25rem] max-h-32"
                style={{ fieldSizing: "content" } as React.CSSProperties}
                disabled={isBusy}
              />
            </GlassCard>
            {isBusy ? (
              <button
                onClick={stop}
                className="flex items-center justify-center w-10 h-10 rounded-xl bg-red-500/20 border border-red-500/30 text-red-500 hover:bg-red-500/30 transition-colors shrink-0"
              >
                <StopCircle className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="flex items-center justify-center w-10 h-10 rounded-xl bg-af-900 dark:bg-af-100 text-white dark:text-af-900 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="text-center text-xs text-af-400 mt-2">
            Flo can make mistakes. Verify important information.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Floating prompt bar ───────────────────────────────────────────────────

export function FloAgentBar() {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Floating bar — bottom center, above the dashboard content */}
      <AnimatePresence>
        {!open && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 w-full max-w-xl px-4"
          >
            <button
              onClick={() => setOpen(true)}
              className="w-full flex items-center gap-3 px-5 py-3.5 rounded-2xl
                bg-white/80 dark:bg-af-900/80 backdrop-blur-md
                border border-white/60 dark:border-af-700/60
                shadow-lg shadow-black/10 dark:shadow-black/30
                hover:bg-white/95 dark:hover:bg-af-800/90
                transition-all duration-200 group"
            >
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-af-900 dark:bg-af-100 shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-white dark:text-af-900" />
              </div>
              <span className="flex-1 text-left text-sm text-af-400 dark:text-af-500">
                Ask Flo anything…
              </span>
              <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-af-100/80 dark:bg-af-800/80 border border-af-200 dark:border-af-700 text-[11px] text-af-500 dark:text-af-400 font-mono">
                ⌘K
              </kbd>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Keyboard shortcut */}
      <KeyboardShortcut onTrigger={() => setOpen(true)} />

      {/* Modal */}
      <AnimatePresence>
        {open && <FloAgentModal onClose={() => setOpen(false)} />}
      </AnimatePresence>
    </>
  );
}

// ─── ⌘K shortcut ──────────────────────────────────────────────────────────

function KeyboardShortcut({ onTrigger }: { onTrigger: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onTrigger();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onTrigger]);
  return null;
}
